/**
 * Transaction endpoints.
 */

import type { CreateTransactionDto, Transaction, UpdateTransactionDto } from '@luminaria/shared';
import { apiClient } from './client';
import { API_ENDPOINTS } from './endpoints';

/**
 * Filters for the transaction list routes. The API requires `page` and
 * `pageSize` together (pageSize ≤ 500), so the type does too.
 */
export type TransactionListParams = {
  symbol?: string;
  /** YYYY-MM-DD */
  startDate?: string;
  /** YYYY-MM-DD */
  endDate?: string;
} & ({ page: number; pageSize: number } | { page?: undefined; pageSize?: undefined });

/** PUT /transactions/:id: `null` clears an optional field. */
export type UpdateTransactionInput = {
  [K in keyof UpdateTransactionDto]?: UpdateTransactionDto[K] | null;
};

export interface ResolveCorporateActionInput {
  id: string;
  ratio?: number;
  relatedSymbol?: string;
  resolution: 'resolved' | 'ignored';
}

export const transactionsApi = {
  async listByAccount(accountId: string, params?: TransactionListParams): Promise<Transaction[]> {
    const { data } = await apiClient.get<{ transactions: Transaction[] }>(API_ENDPOINTS.TRANSACTIONS.BY_ACCOUNT(accountId), { params });
    return data.transactions;
  },

  async listByPortfolio(portfolioId: string, params?: TransactionListParams): Promise<Transaction[]> {
    const { data } = await apiClient.get<{ transactions: Transaction[] }>(API_ENDPOINTS.TRANSACTIONS.BY_PORTFOLIO(portfolioId), { params });
    return data.transactions;
  },

  async get(id: string): Promise<Transaction> {
    const { data } = await apiClient.get<{ transaction: Transaction }>(API_ENDPOINTS.TRANSACTIONS.GET(id));
    return data.transaction;
  },

  async create(accountId: string, dto: CreateTransactionDto): Promise<Transaction> {
    const { data } = await apiClient.post<{ transaction: Transaction }>(API_ENDPOINTS.TRANSACTIONS.BY_ACCOUNT(accountId), dto);
    return data.transaction;
  },

  async update(id: string, dto: UpdateTransactionInput): Promise<Transaction> {
    const { data } = await apiClient.put<{ transaction: Transaction }>(API_ENDPOINTS.TRANSACTIONS.GET(id), dto);
    return data.transaction;
  },

  async remove(id: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.TRANSACTIONS.GET(id));
  },

  async corporateActionsNeedingReview(): Promise<Transaction[]> {
    const { data } = await apiClient.get<{ transactions: Transaction[] }>(API_ENDPOINTS.TRANSACTIONS.CORPORATE_ACTIONS_NEEDING_REVIEW);
    return data.transactions;
  },

  async resolveCorporateAction({ id, ratio, relatedSymbol, resolution }: ResolveCorporateActionInput): Promise<Transaction> {
    const { data } = await apiClient.post<{ transaction: Transaction }>(API_ENDPOINTS.TRANSACTIONS.RESOLVE_CORPORATE_ACTION(id), {
      ratio,
      relatedSymbol,
      resolution,
    });
    return data.transaction;
  },
};
